<div class="inbound-feeds-page">
  <div class="topline">
    <div>
      <h2>Inbound File Feeds</h2>
      <div class="subtitle">Customer-scoped feed definitions, import history, and row-level import errors.</div>
    </div>
    <div class="actions">
      <button mat-stroked-button type="button" (click)="scanUploadedFiles()" [disabled]="scanningUploadedFiles"
        matTooltip="Run the daily scan now to process new uploaded files.">
        <mat-icon>{{scanningUploadedFiles ? 'sync' : 'folder_open'}}</mat-icon>
        {{scanningUploadedFiles ? 'Scanning…' : 'Scan Uploaded Files'}}
      </button>
      <button mat-raised-button color="primary" type="button" (click)="openCreateFeed()">
        <mat-icon>add</mat-icon>
        New Feed
      </button>
    </div>
  </div>

  <div class="inbound-addresses" *ngIf="capabilities">
    <div class="address-item">
      <span class="label">Inbound email</span>
      <strong>{{capabilities.inboundAddresses.email || 'Not configured'}}</strong>
    </div>
    <div class="address-item">
      <span class="label">SFTP host</span>
      <strong>{{setupSftpHost() || 'Not configured'}}</strong>
    </div>
    <div class="address-item">
      <span class="label">Scope options</span>
      <strong>{{getScopeOptionCount()}}</strong>
    </div>
  </div>

  <div class="poll-status-panel" *ngIf="emailPollStatus || pollStatusLoaded">
    <div class="poll-status-header" *ngIf="emailPollStatus; else noPollStatus">
      <div>
        <span class="label">Email Job Runner</span>
        <div class="poll-status-title">
          <strong>{{emailPollStatus.mailbox || capabilities?.inboundAddresses?.email || 'Not configured'}}</strong>
          <span
            *ngIf="emailPollStatus?.lastRunStatus"
            class="status-pill"
            [ngClass]="getPollStatusClass(emailPollStatus)"
          >{{getPollStatusLabel(emailPollStatus)}}</span>
        </div>
        <div class="muted">{{getRunnerSummary(emailPollStatus) || 'Runner details unavailable.'}}</div>
      </div>
      <div class="poll-countdown">
        <div class="poll-status-actions">
          <button mat-stroked-button type="button" (click)="runPollNow()" [disabled]="!canRunPollNow()">
            <mat-icon>{{runningPollNow ? 'sync' : 'play_arrow'}}</mat-icon>
            {{runningPollNow ? 'Running…' : 'Run Now'}}
          </button>
        </div>
        <span class="label">Next poll</span>
        <strong>{{getPollCountdownLabel(emailPollStatus)}}</strong>
        <div class="muted">{{getPollTimestampLabel(emailPollStatus.nextPollAt)}}</div>
      </div>
    </div>

    <div class="poll-status-grid" *ngIf="emailPollStatus">
      <div>
        <span class="label">Last started</span>
        <strong>{{getPollTimestampLabel(emailPollStatus.lastStartedAt)}}</strong>
      </div>
      <div>
        <span class="label">Last finished</span>
        <strong>{{getPollTimestampLabel(emailPollStatus.lastFinishedAt)}}</strong>
      </div>
      <div>
        <span class="label">Last success</span>
        <strong>{{getPollTimestampLabel(emailPollStatus.lastSucceededAt)}}</strong>
      </div>
      <div>
        <span class="label">Last failure</span>
        <strong>{{getPollTimestampLabel(emailPollStatus.lastFailedAt)}}</strong>
      </div>
      <div>
        <span class="label">Last duration</span>
        <strong>{{getPollDurationLabel(emailPollStatus.lastDurationMs)}}</strong>
      </div>
      <div>
        <span class="label">Last refresh</span>
        <strong>{{getRelativeTimestampLabel(emailPollStatus.updatedAt)}}</strong>
      </div>
    </div>

    <div class="poll-status-error" *ngIf="emailPollStatus?.lastErrorMessage">
      <span class="label">Last error</span>
      <div>{{emailPollStatus?.lastErrorMessage}}</div>
    </div>

    <div class="muted" *ngIf="pollStatusRefreshFailed && emailPollStatus">
      Showing the last known runner state. A retry is scheduled automatically.
    </div>

    <ng-template #noPollStatus>
      <div class="empty compact-empty">
        {{loadingPollStatus ? 'Loading email poll status...' : 'Email poll status is not available.'}}
      </div>
    </ng-template>
  </div>

  <section class="filters-panel" aria-label="Filters and search">
    <div class="filters">
      <mat-form-field appearance="fill">
        <mat-label>Status</mat-label>
        <mat-select [(ngModel)]="filters.status" (selectionChange)="loadFeeds()">
          <mat-option value="all">All</mat-option>
          <mat-option *ngFor="let status of capabilities?.statuses || []" [value]="status">{{status}}</mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field appearance="fill">
        <mat-label>Scope type</mat-label>
        <mat-select [(ngModel)]="filters.scopeType" (selectionChange)="loadFeeds()">
          <mat-option value="all">All</mat-option>
          <mat-option *ngFor="let scopeType of capabilities?.scopeTypes || []" [value]="scopeType">{{scopeType}}</mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field appearance="fill">
        <mat-label>Handler</mat-label>
        <mat-select [(ngModel)]="filters.handlerType" (selectionChange)="loadFeeds()">
          <mat-option value="all">All</mat-option>
          <mat-option *ngFor="let handler of capabilities?.handlerTypes || []" [value]="handler">{{getHandlerLabel(handler)}}</mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field appearance="fill">
        <mat-label>Transport</mat-label>
        <mat-select [(ngModel)]="filters.transport" (selectionChange)="loadFeeds()">
          <mat-option value="all">All</mat-option>
          <mat-option *ngFor="let transport of capabilities?.transports || []" [value]="transport">{{transport}}</mat-option>
        </mat-select>
      </mat-form-field>
      <mat-form-field appearance="fill" class="search-field">
        <mat-label>Feed search</mat-label>
        <input matInput [(ngModel)]="filters.filter" (keyup.enter)="loadFeeds()" placeholder="Name, key, description, scope ID">
        <button mat-icon-button matSuffix type="button" aria-label="Search feeds" (click)="loadFeeds()">
          <mat-icon>search</mat-icon>
        </button>
      </mat-form-field>
    </div>

    <div class="scope-search">
      <mat-form-field appearance="fill">
        <mat-label>Scope type</mat-label>
        <mat-select [(ngModel)]="scopeOptionType" (selectionChange)="onScopeOptionTypeChange()">
          <mat-option *ngFor="let scopeType of capabilities?.scopeTypes || []" [value]="scopeType">{{scopeType}}</mat-option>
        </mat-select>
        <mat-hint>Scope options are loaded by type to keep the query bounded.</mat-hint>
      </mat-form-field>
      <mat-form-field appearance="fill">
        <mat-label>Scope option search</mat-label>
        <input matInput [(ngModel)]="scopeSearch" (ngModelChange)="onScopeSearchChange()" placeholder="PLX, company name, vendor product">
        <mat-hint>Optional. Results update automatically while you type.</mat-hint>
      </mat-form-field>
    </div>
  </section>

  <mat-tab-group [(selectedIndex)]="selectedTabIndex" (selectedTabChange)="$event.tab.textLabel === 'SFTP uploads' && loadSftpUploads()">
    <mat-tab label="Feeds">
      <div class="sftp-setup-prompt" *ngIf="sftpSetupFeed as newFeed" role="status">
        <span>Feed saved. Set up SFTP access for {{newFeed.name}}.</span>
        <button mat-stroked-button type="button" (click)="openSftpAccess(newFeed)">Set up SFTP access</button>
      </div>
      <div class="table-wrap">
        <table mat-table matSort #feedsSort="matSort" [dataSource]="feeds" class="table">
          <ng-container matColumnDef="name">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Name</th>
            <td mat-cell *matCellDef="let feed">
              <button mat-button type="button" class="link-button" (click)="$event.stopPropagation(); onFeedSelected(feed)">
                {{feed.name}}
              </button>
              <div class="muted mono">{{feed.feedKey}}</div>
            </td>
          </ng-container>

          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
            <td mat-cell *matCellDef="let feed">
              <span class="status-pill" [class.status-active]="feed.status === 'active'" [class.status-paused]="feed.status === 'paused'" [class.status-archived]="feed.status === 'archived'">{{feed.status}}</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="scope">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Scope</th>
            <td mat-cell *matCellDef="let feed">{{getScopeLabel(feed)}}</td>
          </ng-container>

          <ng-container matColumnDef="handlerType">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Handler type</th>
            <td mat-cell *matCellDef="let feed">{{getHandlerLabel(feed.handlerType)}}</td>
          </ng-container>

          <ng-container matColumnDef="transports">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Transports</th>
            <td mat-cell *matCellDef="let feed">
              <div *ngIf="feed.transportConfig?.sftp" class="sftp-row-access">
                <span>{{feed.transportConfig.sftp.processingMode === 'upload' ? 'On upload completion' : 'Daily scan'}}</span>
                <span class="status-pill" [class.status-active]="sftpStatuses[feed.id] === 'ready'">{{sftpStatusLabel(feed)}}</span>
              </div>
              <mat-chip-listbox aria-label="Configured transports">
                <mat-chip-option *ngFor="let transport of getTransportLabels(feed)" disabled>{{transport}}</mat-chip-option>
              </mat-chip-listbox>
            </td>
          </ng-container>

          <ng-container matColumnDef="lastImportStatus">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Last import status</th>
            <td mat-cell *matCellDef="let feed">{{feed.lastImportStatus || '-'}}</td>
          </ng-container>

          <ng-container matColumnDef="lastImportAt">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Last import time</th>
            <td mat-cell *matCellDef="let feed">{{feed.lastImportAt || '-'}}</td>
          </ng-container>

          <ng-container matColumnDef="updatedAt">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Updated time</th>
            <td mat-cell *matCellDef="let feed">{{feed.updatedAt || '-'}}</td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef>Actions</th>
            <td mat-cell *matCellDef="let feed">
              <button
                mat-icon-button
                [matMenuTriggerFor]="feedMenu"
                [matMenuTriggerData]="{feed: feed}"
                aria-label="Feed actions"
                (click)="$event.stopPropagation()"
              >
                <mat-icon>more_vert</mat-icon>
              </button>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="feedColumns"></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: feedColumns"
            class="feed-row"
            [class.selected-row]="isSelectedFeed(row)"
            (click)="onFeedSelected(row)"
          ></tr>
        </table>
        <div class="loading" *ngIf="loadingFeeds">Loading feeds...</div>
        <div class="empty" *ngIf="!loadingFeeds && feeds.data.length === 0">No inbound feeds match the current filters.</div>
      </div>
    </mat-tab>

    <mat-tab label="Unmatched Messages">
      <div class="tab-toolbar">
        <mat-form-field appearance="fill">
          <mat-label>Status</mat-label>
          <mat-select [(ngModel)]="unmatchedMessageFilters.status" (selectionChange)="loadUnmatchedMessages()">
            <mat-option value="open">Open</mat-option>
            <mat-option value="resolved">Resolved</mat-option>
            <mat-option value="ignored">Ignored</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="fill">
          <mat-label>Transport</mat-label>
          <mat-select [(ngModel)]="unmatchedMessageFilters.transport" (selectionChange)="loadUnmatchedMessages()">
            <mat-option value="all">All</mat-option>
            <mat-option value="email">Email</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="fill" class="search-field">
          <mat-label>Inbox search</mat-label>
          <input
            matInput
            [(ngModel)]="unmatchedMessageFilters.filter"
            (keyup.enter)="loadUnmatchedMessages()"
            placeholder="Sender, subject, file name, error"
          >
          <button mat-icon-button matSuffix type="button" aria-label="Search unmatched messages" (click)="loadUnmatchedMessages()">
            <mat-icon>search</mat-icon>
          </button>
        </mat-form-field>
      </div>

      <div class="table-wrap">
        <table mat-table matSort #unmatchedMessagesSort="matSort" [dataSource]="unmatchedMessages" class="table">
          <ng-container matColumnDef="createdAt">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Created</th>
            <td mat-cell *matCellDef="let message">{{getPollTimestampLabel(message.createdAt)}}</td>
          </ng-container>

          <ng-container matColumnDef="transport">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Transport</th>
            <td mat-cell *matCellDef="let message">{{message.transport || '-'}}</td>
          </ng-container>

          <ng-container matColumnDef="sourceIdentifier">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Source</th>
            <td mat-cell *matCellDef="let message">{{message.sourceIdentifier || '-'}}</td>
          </ng-container>

          <ng-container matColumnDef="subject">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Subject</th>
            <td mat-cell *matCellDef="let message">{{message.subject || '-'}}</td>
          </ng-container>

          <ng-container matColumnDef="originalFileName">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Original file</th>
            <td mat-cell *matCellDef="let message">{{message.originalFileName || '-'}}</td>
          </ng-container>

          <ng-container matColumnDef="errorCode">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Error code</th>
            <td mat-cell *matCellDef="let message">{{message.errorCode || '-'}}</td>
          </ng-container>

          <ng-container matColumnDef="message">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Message</th>
            <td mat-cell *matCellDef="let message">{{message.message}}</td>
          </ng-container>

          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
            <td mat-cell *matCellDef="let message">
              <span class="status-pill" [ngClass]="getUnmatchedStatusClass(message.status)">{{message.status}}</span>
            </td>
          </ng-container>

          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef>Actions</th>
            <td mat-cell *matCellDef="let message">
              <button
                mat-icon-button
                [matMenuTriggerFor]="unmatchedMessageMenu"
                [matMenuTriggerData]="{message: message}"
                aria-label="Unmatched message actions"
                (click)="$event.stopPropagation()"
              >
                <mat-icon>more_vert</mat-icon>
              </button>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="unmatchedMessageColumns"></tr>
          <tr
            mat-row
            *matRowDef="let row; columns: unmatchedMessageColumns"
            [class.selected-row]="isSelectedUnmatchedMessage(row)"
            (click)="selectUnmatchedMessage(row)"
          ></tr>
        </table>
        <div class="loading" *ngIf="loadingUnmatchedMessages">Loading unmatched messages...</div>
        <div class="empty" *ngIf="!loadingUnmatchedMessages && unmatchedMessages.data.length === 0">
          No unmatched messages match the current filters.
        </div>
      </div>

      <div class="detail" *ngIf="selectedUnmatchedMessage; else noUnmatchedMessageSelected">
        <div class="detail-header">
          <div>
            <h3>{{selectedUnmatchedMessage.subject || selectedUnmatchedMessage.sourceIdentifier || 'Unmatched message'}}</h3>
            <div class="muted">{{selectedUnmatchedMessage.message}}</div>
          </div>
          <div class="actions">
            <button mat-stroked-button type="button" (click)="openUnmatchedMessageStatusDialog(selectedUnmatchedMessage, 'resolved')">
              <mat-icon>check_circle</mat-icon>
              Resolve
            </button>
            <button mat-stroked-button type="button" (click)="openUnmatchedMessageStatusDialog(selectedUnmatchedMessage, 'ignored')">
              <mat-icon>block</mat-icon>
              Ignore
            </button>
            <button mat-stroked-button type="button" (click)="openUnmatchedMessageStatusDialog(selectedUnmatchedMessage, 'open')">
              <mat-icon>undo</mat-icon>
              Re-open
            </button>
          </div>
        </div>

        <div class="summary-grid">
          <div><span class="label">Created</span><strong>{{getPollTimestampLabel(selectedUnmatchedMessage.createdAt)}}</strong></div>
          <div><span class="label">Transport</span><strong>{{selectedUnmatchedMessage.transport || '-'}}</strong></div>
          <div><span class="label">Source identifier</span><strong>{{selectedUnmatchedMessage.sourceIdentifier || '-'}}</strong></div>
          <div><span class="label">Subject</span><strong>{{selectedUnmatchedMessage.subject || '-'}}</strong></div>
          <div><span class="label">Original file name</span><strong>{{selectedUnmatchedMessage.originalFileName || '-'}}</strong></div>
          <div><span class="label">Error code</span><strong>{{selectedUnmatchedMessage.errorCode || '-'}}</strong></div>
          <div><span class="label">Checksum</span><strong class="mono">{{selectedUnmatchedMessage.checksum || '-'}}</strong></div>
          <div>
            <span class="label">Status</span>
            <strong><span class="status-pill" [ngClass]="getUnmatchedStatusClass(selectedUnmatchedMessage.status)">{{selectedUnmatchedMessage.status}}</span></strong>
          </div>
        </div>

        <div class="config-grid">
          <section>
            <h4>Attachments</h4>
            <div *ngIf="getUnmatchedAttachments(selectedUnmatchedMessage).length; else noAttachments">
              <div class="attachment-list">
                <div class="attachment-item" *ngFor="let attachment of getUnmatchedAttachments(selectedUnmatchedMessage)">
                  <pre>{{formatJson(attachment)}}</pre>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h4>Resolution</h4>
            <p><span class="label">Resolution note</span>{{selectedUnmatchedMessage.resolutionNote || '-'}}</p>
            <p><span class="label">Resolved at</span>{{getPollTimestampLabel(selectedUnmatchedMessage.resolvedAt)}}</p>
            <p><span class="label">Resolved by user ID</span>{{selectedUnmatchedMessage.resolvedByUserId || '-'}}</p>
          </section>
        </div>

        <section class="format-panel" *ngIf="selectedUnmatchedMessage.bodyText">
          <h4>Body Text</h4>
          <pre>{{selectedUnmatchedMessage.bodyText}}</pre>
        </section>

        <ng-template #noAttachments>
          <p class="muted">No attachments metadata was returned for this message.</p>
        </ng-template>
      </div>

      <ng-template #noUnmatchedMessageSelected>
        <div class="empty detail-empty">Select a message to review unmatched-email details and triage it.</div>
      </ng-template>
    </mat-tab>

    <mat-tab label="Handler Formats">
      <div class="formats-tab" *ngIf="handlerFormatItems.length; else noHandlerFormats">
        <mat-accordion>
          <mat-expansion-panel *ngFor="let item of handlerFormatItems" [expanded]="selectedFeed?.handlerType === item.handlerType">
            <mat-expansion-panel-header>
              <mat-panel-title>{{item.format.label || getHandlerLabel(item.handlerType)}}</mat-panel-title>
              <mat-panel-description>
                {{getRequiredScopeLabel(item.format)}} - {{getFileTypesLabel(item.format)}}
              </mat-panel-description>
            </mat-expansion-panel-header>

            <div class="format-header">
              <div class="muted">{{item.format.behavior || 'No processing notes provided.'}}</div>
              <div class="format-meta">
                <span><strong>Handler:</strong> <span class="mono">{{item.handlerType}}</span></span>
                <span><strong>Header row:</strong> {{getHeaderRowLabel(item.format)}}</span>
                <span><strong>Reject extra columns:</strong> {{getRejectUnknownColumnsLabel(item.format)}}</span>
              </div>
            </div>
            <div class="format-actions" *ngIf="canDownloadTemplate(item)">
              <button
                *ngFor="let downloadType of item.downloadTypes"
                mat-stroked-button
                type="button"
                (click)="downloadTemplate(item, downloadType)"
              >
                <mat-icon>download</mat-icon>
                {{downloadType.toUpperCase()}} template
              </button>
            </div>

            <div *ngIf="item.format.fields?.length; else noFormatFields">
              <div class="format-field-group" *ngIf="item.requiredFields.length">
                <h5>Required Columns</h5>
                <div class="format-table-wrap">
                  <table class="format-table">
                    <thead>
                    <tr>
                      <th>Column</th>
                      <th>Required</th>
                      <th>Type</th>
                      <th>Formats / values</th>
                      <th>Description</th>
                      <th>Maps to</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr *ngFor="let field of item.requiredFields">
                      <td class="mono">{{field.name}}</td>
                      <td>{{getFieldRequiredLabel(field)}}</td>
                      <td>{{field.type || '-'}}</td>
                      <td>
                        <div>{{getFieldFormatsLabel(field)}}</div>
                        <div class="muted" *ngIf="getFieldAcceptedValuesLabel(field) !== '-'">{{getFieldAcceptedValuesLabel(field)}}</div>
                      </td>
                      <td>{{field.description || '-'}}</td>
                      <td class="mono">{{field.mapsTo || '-'}}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <div class="format-field-group" *ngIf="item.optionalFields.length">
                <h5>Optional Columns</h5>
                <div class="format-table-wrap">
                  <table class="format-table">
                    <thead>
                    <tr>
                      <th>Column</th>
                      <th>Required</th>
                      <th>Type</th>
                      <th>Formats / values</th>
                      <th>Description</th>
                      <th>Maps to</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr *ngFor="let field of item.optionalFields">
                      <td class="mono">{{field.name}}</td>
                      <td>{{getFieldRequiredLabel(field)}}</td>
                      <td>{{field.type || '-'}}</td>
                      <td>
                        <div>{{getFieldFormatsLabel(field)}}</div>
                        <div class="muted" *ngIf="getFieldAcceptedValuesLabel(field) !== '-'">{{getFieldAcceptedValuesLabel(field)}}</div>
                      </td>
                      <td>{{field.description || '-'}}</td>
                      <td class="mono">{{field.mapsTo || '-'}}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <ng-template #noFormatFields>
              <div class="empty compact-empty">No business columns are required for this handler.</div>
            </ng-template>
          </mat-expansion-panel>
        </mat-accordion>
      </div>
      <ng-template #noHandlerFormats>
        <div class="empty detail-empty">No handler format metadata was returned by the backend.</div>
      </ng-template>
    </mat-tab>

    <mat-tab label="Imports">
      <div class="tab-toolbar" *ngIf="selectedFeed">
        <mat-form-field appearance="fill">
          <mat-label>Import status</mat-label>
          <mat-select [(ngModel)]="importFilters.status" (selectionChange)="loadImports()">
            <mat-option value="all">All</mat-option>
            <mat-option *ngFor="let status of capabilities?.importStatuses || []" [value]="status">{{status}}</mat-option>
          </mat-select>
        </mat-form-field>
      </div>
      <div class="table-wrap">
        <table mat-table matSort #importsSort="matSort" [dataSource]="imports" class="table">
          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
            <td mat-cell *matCellDef="let item">{{item.status}}</td>
          </ng-container>
          <ng-container matColumnDef="transport">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Transport</th>
            <td mat-cell *matCellDef="let item">{{item.transport}}</td>
          </ng-container>
          <ng-container matColumnDef="originalFileName">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>File</th>
            <td mat-cell *matCellDef="let item">{{item.originalFileName || '-'}}</td>
          </ng-container>
          <ng-container matColumnDef="sourceIdentifier">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Source</th>
            <td mat-cell *matCellDef="let item">{{item.sourceIdentifier || '-'}}</td>
          </ng-container>
          <ng-container matColumnDef="counts">
            <th mat-header-cell *matHeaderCellDef>Counts</th>
            <td mat-cell *matCellDef="let item">
              {{item.successCount || 0}} ok / {{item.errorCount || 0}} errors / {{item.warningCount || 0}} warnings
            </td>
          </ng-container>
          <ng-container matColumnDef="receivedAt">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Received</th>
            <td mat-cell *matCellDef="let item">{{item.receivedAt || '-'}}</td>
          </ng-container>
          <ng-container matColumnDef="finishedAt">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Finished</th>
            <td mat-cell *matCellDef="let item">{{item.finishedAt || '-'}}</td>
          </ng-container>
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef>Actions</th>
            <td mat-cell *matCellDef="let item">
              <button
                mat-icon-button
                [matMenuTriggerFor]="importMenu"
                [matMenuTriggerData]="{item: item}"
                aria-label="Import actions"
                (click)="$event.stopPropagation()"
              >
                <mat-icon>more_vert</mat-icon>
              </button>
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="importColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: importColumns" [class.selected-row]="selectedImport?.id === row.id" (click)="selectImport(row)"></tr>
        </table>
        <div class="empty" *ngIf="selectedFeed && imports.data.length === 0">No imports for this feed.</div>
        <div class="empty" *ngIf="!selectedFeed">Select a feed to view imports.</div>
      </div>
    </mat-tab>

    <mat-tab label="Errors">
      <div class="tab-toolbar" *ngIf="selectedFeed">
        <mat-form-field appearance="fill">
          <mat-label>Error status</mat-label>
          <mat-select [(ngModel)]="errorFilters.status" (selectionChange)="loadErrors(selectedImport?.id)">
            <mat-option value="all">All</mat-option>
            <mat-option *ngFor="let status of capabilities?.errorStatuses || []" [value]="status">{{status}}</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field appearance="fill">
          <mat-label>Severity</mat-label>
          <mat-select [(ngModel)]="errorFilters.severity" (selectionChange)="loadErrors(selectedImport?.id)">
            <mat-option value="all">All</mat-option>
            <mat-option *ngFor="let severity of capabilities?.errorSeverities || []" [value]="severity">{{severity}}</mat-option>
          </mat-select>
        </mat-form-field>
        <button mat-stroked-button type="button" (click)="selectedImport = null; loadErrors()">
          <mat-icon>filter_alt_off</mat-icon>
          All Feed Errors
        </button>
        <span class="muted" *ngIf="selectedImport">Filtered to import {{selectedImport.id}}</span>
      </div>
      <div class="table-wrap">
        <table mat-table matSort #errorsSort="matSort" [dataSource]="errors" class="table">
          <ng-container matColumnDef="rowNumber">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Row</th>
            <td mat-cell *matCellDef="let error">{{error.rowNumber || '-'}}</td>
          </ng-container>
          <ng-container matColumnDef="fieldName">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Field</th>
            <td mat-cell *matCellDef="let error">{{error.fieldName || '-'}}</td>
          </ng-container>
          <ng-container matColumnDef="severity">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Severity</th>
            <td mat-cell *matCellDef="let error">{{error.severity}}</td>
          </ng-container>
          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Status</th>
            <td mat-cell *matCellDef="let error">{{error.status}}</td>
          </ng-container>
          <ng-container matColumnDef="message">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Message</th>
            <td mat-cell *matCellDef="let error">{{error.message}}</td>
          </ng-container>
          <ng-container matColumnDef="resolutionNote">
            <th mat-header-cell *matHeaderCellDef mat-sort-header>Resolution note</th>
            <td mat-cell *matCellDef="let error">{{error.resolutionNote || '-'}}</td>
          </ng-container>
          <ng-container matColumnDef="actions">
            <th mat-header-cell *matHeaderCellDef>Actions</th>
            <td mat-cell *matCellDef="let error">
              <button
                mat-icon-button
                [matMenuTriggerFor]="errorMenu"
                [matMenuTriggerData]="{error: error}"
                aria-label="Error actions"
                (click)="$event.stopPropagation()"
              >
                <mat-icon>more_vert</mat-icon>
              </button>
            </td>
          </ng-container>
          <tr mat-header-row *matHeaderRowDef="errorColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: errorColumns"></tr>
        </table>
        <div class="empty" *ngIf="selectedFeed && errors.data.length === 0">No errors match the current filters.</div>
        <div class="empty" *ngIf="!selectedFeed">Select a feed to view errors.</div>
      </div>
    </mat-tab>
    <mat-tab label="SFTP uploads">
      <div class="sftp-upload-history">
        <p>Recent SFTP files recorded by scheduled scans, Scan Uploaded Files, or upload-completion processing. Each file links to its import attempts. Files handled by scans appear once the scan discovers them.</p>
        <button mat-stroked-button type="button" (click)="loadSftpUploads()" [disabled]="loadingSftpUploads">Refresh uploads</button>
        <p *ngIf="sftpUploadsError" role="alert">{{sftpUploadsError}}</p>
        <p *ngIf="!loadingSftpUploads && !sftpUploadsError && !sftpUploads.length">No SFTP files recorded.</p>
        <div class="sftp-upload-entry" *ngFor="let upload of sftpUploads">
          <strong>{{upload.filePath || upload.id}}</strong>
          <p>Processing: {{upload.processingMode === 'upload' ? 'On upload completion' : 'Scheduled / manual scan'}}</p>
          <p>Status: {{upload.status}} · Attempts: {{upload.attempts}} · Recoveries: {{upload.recoveryCount}}</p>
          <p>Uploaded: {{upload.uploadedAt || 'Not recorded'}} · First seen: {{upload.firstSeenAt}}</p>
          <p *ngIf="upload.queuedAt">Queued: {{upload.queuedAt}}</p>
          <p>Processing started: {{upload.startedAt || '-'}} · Finished: {{upload.finishedAt || '-'}}</p>
          <p *ngIf="upload.nextAttemptAt">Next attempt: {{upload.nextAttemptAt}}</p>
          <div class="sftp-import-attempts" *ngIf="upload.importAttempts.length">
            <span class="label">Import attempts</span>
            <button mat-button type="button" *ngFor="let attempt of upload.importAttempts"
              (click)="openSftpImport(attempt.id)">{{attempt.id}} · {{attempt.status}}</button>
          </div>
          <p *ngIf="upload.errorCode" role="status">{{upload.errorCode}}</p>
          <button mat-stroked-button type="button" *ngIf="upload.canRetry"
            [disabled]="!!retryingUpload" (click)="retrySftpUpload(upload)">Retry upload</button>
        </div>
      </div>
    </mat-tab>
  </mat-tab-group>

  <mat-menu #feedMenu="matMenu">
    <ng-template matMenuContent let-feed="feed">
      <button mat-menu-item *ngIf="feed.transportConfig?.sftp" (click)="browseFiles(feed)">
        <mat-icon>folder_open</mat-icon>
        <span>Browse files</span>
      </button>
      <button mat-menu-item *ngIf="feed.transportConfig?.sftp" (click)="openSftpAccess(feed)">
        <mat-icon>settings</mat-icon>
        <span>Manage SFTP</span>
      </button>
      <button mat-menu-item (click)="showFeedImports(feed)">
        <mat-icon>list_alt</mat-icon>
        <span>Show Imports</span>
      </button>
      <button mat-menu-item (click)="showFeedErrors(feed)">
        <mat-icon>report_problem</mat-icon>
        <span>Show Errors</span>
      </button>
      <button mat-menu-item (click)="openEditFeed(feed)">
        <mat-icon>edit</mat-icon>
        <span>Edit</span>
      </button>
      <button mat-menu-item *ngIf="feed.status === 'archived'" (click)="restoreFeed(feed, 'active')">
        <mat-icon>unarchive</mat-icon>
        <span>Restore active</span>
      </button>
      <button mat-menu-item *ngIf="feed.status !== 'archived'" (click)="archiveFeed(feed)">
        <mat-icon>archive</mat-icon>
        <span>Archive</span>
      </button>
    </ng-template>
  </mat-menu>

  <mat-menu #importMenu="matMenu">
    <ng-template matMenuContent let-item="item">
      <button mat-menu-item (click)="openImportDetails(item)">
        <mat-icon>visibility</mat-icon>
        <span>View details</span>
      </button>
      <button mat-menu-item (click)="showImportErrors(item)">
        <mat-icon>report_problem</mat-icon>
        <span>View errors</span>
      </button>
    </ng-template>
  </mat-menu>

  <mat-menu #errorMenu="matMenu">
    <ng-template matMenuContent let-error="error">
      <button mat-menu-item (click)="openErrorDetails(error)">
        <mat-icon>visibility</mat-icon>
        <span>View details</span>
      </button>
      <button mat-menu-item (click)="openErrorStatusDialog(error, 'resolved')">
        <mat-icon>check_circle</mat-icon>
        <span>Resolve</span>
      </button>
      <button mat-menu-item (click)="openErrorStatusDialog(error, 'ignored')">
        <mat-icon>block</mat-icon>
        <span>Ignore</span>
      </button>
      <button mat-menu-item (click)="openErrorStatusDialog(error, 'open')">
        <mat-icon>undo</mat-icon>
        <span>Reopen</span>
      </button>
    </ng-template>
  </mat-menu>

  <mat-menu #unmatchedMessageMenu="matMenu">
    <ng-template matMenuContent let-message="message">
      <button mat-menu-item (click)="selectUnmatchedMessage(message)">
        <mat-icon>visibility</mat-icon>
        <span>View details</span>
      </button>
      <button mat-menu-item (click)="openUnmatchedMessageStatusDialog(message, 'resolved')">
        <mat-icon>check_circle</mat-icon>
        <span>Resolve</span>
      </button>
      <button mat-menu-item (click)="openUnmatchedMessageStatusDialog(message, 'ignored')">
        <mat-icon>block</mat-icon>
        <span>Ignore</span>
      </button>
      <button mat-menu-item (click)="openUnmatchedMessageStatusDialog(message, 'open')">
        <mat-icon>undo</mat-icon>
        <span>Re-open</span>
      </button>
    </ng-template>
  </mat-menu>
</div>

<ng-template #sftpPanel>
  <div class="sftp-panel-content">
    <div class="sftp-panel-header">
      <h2>SFTP access</h2>
      <button mat-icon-button type="button" aria-label="Close SFTP access" [disabled]="sftpBusy" (click)="closeSftpAccess()"><mat-icon>close</mat-icon></button>
    </div>
    <app-inbound-sftp-access [feed]="sftpFeed" (busyChanged)="onSftpBusy($event)" (statusChanged)="onSftpStatus($event)" (feedUpdated)="onSftpFeedUpdated($event)"></app-inbound-sftp-access>
  </div>
</ng-template>

<ng-template #filesPanel>
  <div class="sftp-panel-content files-panel" cdkDrag cdkDragRootElement=".cdk-overlay-pane" cdkDragBoundary=".cdk-overlay-container">
    <div class="sftp-panel-header">
      <h2 cdkDragHandle title="Drag to move">Browse files — {{filesFeed?.name}}</h2>
      <button mat-icon-button type="button" [disabled]="filesBusy" aria-label="Close file browser" (click)="closeFiles()"><mat-icon>close</mat-icon></button>
    </div>
    <p class="files-folder">Directory: {{filePage?.folder || filesFeed?.transportConfig?.sftp?.folder}}</p>
    <button mat-stroked-button type="button" [disabled]="filesLoading || filesBusy" (click)="loadFiles()">Refresh</button>
    <input #fileUpload type="file" hidden (change)="addFile($event)">
    <button mat-flat-button color="primary" type="button" class="add-file-button" title="Upload a file up to 25 MB to this directory" [disabled]="filesLoading || filesBusy" (click)="fileUpload.click()">Add File</button>
    <p *ngIf="filesBusy" role="status">Working…</p>
    <p *ngIf="filesLoading" role="status">Loading files…</p>
    <p *ngIf="filesError" role="alert">{{filesError}}</p>
    <ng-container *ngIf="filePage as page">
      <p *ngIf="!filesLoading && !filesError && !page.files.length && !page.nextToken">No files in this directory.</p>
      <div class="files-table-wrap" *ngIf="page.files.length">
        <table class="files-table" matSort [matSortActive]="filesSort.active" [matSortDirection]="filesSort.direction"
          [matSortDisableClear]="true" (matSortChange)="sortFiles($event)">
          <thead><tr>
            <th scope="col" mat-sort-header="name" sortActionDescription="Sort by filename">Filename</th>
            <th scope="col" mat-sort-header="size" sortActionDescription="Sort by size">Size (bytes)</th>
            <th scope="col" mat-sort-header="lastModified" sortActionDescription="Sort by last modified">Last modified</th>
            <th scope="col">Processing status</th>
            <th scope="col" class="file-row-actions" aria-label="File actions"></th>
          </tr></thead>
          <tbody><tr *ngFor="let file of page.files">
            <td>{{file.name}}</td>
            <td>{{file.size | number}}</td>
            <td>{{file.lastModified | date:'medium'}}</td>
            <td><span class="status-pill" [class.status-active]="file.processingStatus === 'processed'"
              [class.file-status-error]="file.processingStatus === 'errors'">{{fileProcessingStatusLabel(file.processingStatus)}}</span></td>
            <td class="file-row-actions">
              <button mat-icon-button type="button" [matMenuTriggerFor]="fileMenu" [disabled]="filesBusy || filesLoading" [attr.aria-label]="'File actions for ' + file.name"><mat-icon>more_vert</mat-icon></button>
              <mat-menu #fileMenu="matMenu">
                <button mat-menu-item (click)="openFile(file, 'view')"><mat-icon>visibility</mat-icon><span>View</span></button>
                <button mat-menu-item (click)="openFile(file, 'download')"><mat-icon>download</mat-icon><span>Download</span></button>
                <button mat-menu-item (click)="beginFileAction('move', file)"><mat-icon>drive_file_move</mat-icon><span>Move</span></button>
                <button mat-menu-item (click)="beginFileAction('rename', file)"><mat-icon>edit</mat-icon><span>Rename</span></button>
                <button mat-menu-item (click)="deleteFile(file)"><mat-icon>delete</mat-icon><span>Delete</span></button>
              </mat-menu>
            </td>
          </tr></tbody>
        </table>
      </div>
      <p *ngIf="page.nextToken">Sorting applies to loaded files. Load more to include additional files.</p>
      <button mat-stroked-button type="button" *ngIf="page.nextToken" [disabled]="filesLoading || filesBusy" (click)="loadFiles(true)">Load more</button>
    </ng-container>
  </div>
</ng-template>

<ng-template #deleteFilePanel let-file>
  <h2 mat-dialog-title>Delete file?</h2>
  <mat-dialog-content>
    <p>Delete <strong class="delete-file-name">{{file.name}}</strong> from the upload directory? This cannot be undone.</p>
  </mat-dialog-content>
  <mat-dialog-actions align="end" class="delete-file-actions">
    <button mat-button type="button" [mat-dialog-close]="false" cdkFocusInitial>Cancel</button>
    <button mat-flat-button color="warn" type="button" [mat-dialog-close]="true">Delete file</button>
  </mat-dialog-actions>
</ng-template>

<ng-template #fileActionPanel>
  <form class="file-action-form" (ngSubmit)="submitFileAction()">
    <h2 mat-dialog-title>{{fileAction === 'rename' ? 'Rename file' : 'Move file'}}</h2>
    <mat-dialog-content>
      <p class="file-action-name">{{actionFile?.name}}</p>
      <mat-form-field appearance="outline">
        <mat-label>{{fileAction === 'rename' ? 'New filename' : 'Destination folder'}}</mat-label>
        <input matInput name="fileDestination" [(ngModel)]="fileDestination" [disabled]="filesBusy" [required]="fileAction === 'rename'" cdkFocusInitial>
        <mat-hint *ngIf="fileAction === 'move'">Relative to this feed’s directory. Leave empty for the root directory.</mat-hint>
      </mat-form-field>
      <p *ngIf="filesBusy" role="status">{{fileAction === 'rename' ? 'Renaming file…' : 'Moving file…'}}</p>
      <p *ngIf="filesError" role="alert">{{filesError}}</p>
    </mat-dialog-content>
    <mat-dialog-actions align="end" class="file-action-buttons">
      <button mat-button type="button" [disabled]="filesBusy" (click)="cancelFileAction()">Cancel</button>
      <button mat-flat-button color="primary" type="submit" [disabled]="filesBusy || filesLoading">{{fileAction === 'rename' ? 'Rename file' : 'Move file'}}</button>
    </mat-dialog-actions>
  </form>
</ng-template>
